import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import Select from "react-select";

const CommandCenter = ({ isSidebarOpen }) => {
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [cardStates, setCardStates] = useState({
    tool1: false,
    tool2: false,
    tool3: false,
    tool4: false,
    tool5: false,
    tool6: false,
  });
  const [lastUpdated, setLastUpdated] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [instanceId, setInstanceId] = useState('');
  const [detectedIp, setDetectedIp] = useState('');
  const [userIp, setUserIp] = useState('');
  const [isStarting, setIsStarting] = useState(false);

  // This effect is no longer needed as we're fetching accounts directly from the API

  // Load states from localStorage on component mount
  useEffect(() => {
    const loadStates = () => {
      try {
        const savedStates = localStorage.getItem("cardStates");
        const savedLastUpdated = localStorage.getItem("lastUpdated");
        const accounts = localStorage.getItem("connectedAccounts");

        if (savedStates) {
          const parsedStates = JSON.parse(savedStates);
          // Validate the structure of saved states
          const validStates = {
            tool1: typeof parsedStates.tool1 === 'boolean' ? parsedStates.tool1 : false,
            tool2: typeof parsedStates.tool2 === 'boolean' ? parsedStates.tool2 : false,
            tool3: typeof parsedStates.tool3 === 'boolean' ? parsedStates.tool3 : false,
            tool4: typeof parsedStates.tool4 === 'boolean' ? parsedStates.tool4 : false,
            tool5: typeof parsedStates.tool5 === 'boolean' ? parsedStates.tool5 : false,
            tool6: typeof parsedStates.tool6 === 'boolean' ? parsedStates.tool6 : false,
          };
          setCardStates(validStates);
        }

        if (savedLastUpdated) {
          try {
            setLastUpdated(JSON.parse(savedLastUpdated));
          } catch (error) {
            console.error("Error parsing last updated:", error);
            setLastUpdated({});
          }
        }

        setConnectedAccounts(accounts ? JSON.parse(accounts) : []);
      } catch (error) {
        console.error("Error loading states:", error);
        // Reset to default states if there's an error
        setCardStates({
          tool1: false,
          tool2: false,
          tool3: false,
          tool4: false,
          tool5: false,
          tool6: false,
        });
      }
    };

    loadStates();
  }, []);

  // Fetch account details from the API
  useEffect(() => {
    const fetchAccounts = async () => {
      setIsLoadingAccounts(true);
      try {
        const organizationID = localStorage.getItem("organizationID");
        const apiDomain = process.env.REACT_APP_API_DOMAIN || "https://u3lrzohpy7.execute-api.us-west-2.amazonaws.com/prod";
        
        const response = await fetch(`${apiDomain}/getUser`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ organizationID }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        const userData = await response.json();
        
        if (userData.cloudAccounts) {
          const cloudAccountsData = JSON.parse(userData.cloudAccounts);
          
          // Process AWS accounts from the response
          if (cloudAccountsData.aws && Array.isArray(cloudAccountsData.aws)) {
            const formattedAccounts = cloudAccountsData.aws.map(account => {
              // Extract AWS account ID from IAM role ARN
              const arnParts = account.iamrole.split(":");
              const awsAccountId = arnParts.length >= 5 ? arnParts[4] : "Unknown";
              
              return {
                id: awsAccountId,
                name: "AWS",
                description: account.alias || awsAccountId, // Use alias if available, otherwise use account ID
                logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg",
                iamrole: account.iamrole,
                awsAccountId: awsAccountId,
                alias: account.alias || awsAccountId
              };
            });
            
            setConnectedAccounts(formattedAccounts);
            // Also update localStorage for other components to use
            localStorage.setItem("connectedAccounts", JSON.stringify(formattedAccounts));
          }
        }
      } catch (error) {
        console.error("Error fetching accounts:", error);
        // Try to fall back to localStorage if API fails
        const storedAccounts = localStorage.getItem("connectedAccounts");
        if (storedAccounts) {
          try {
            setConnectedAccounts(JSON.parse(storedAccounts));
          } catch (parseError) {
            console.error("Error parsing stored accounts:", parseError);
          }
        }
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    fetchAccounts();
  }, []);

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => {
        setDetectedIp(data.ip);
        setUserIp(data.ip);
      })
      .catch(err => console.error(err));
  }, []);

  const handleCardClick = (toolKey, toolName) => {
    const currentState = cardStates[toolKey];
    const action = currentState ? "deactivate" : "activate";
    const confirmationMessage = `Are you sure you want to ${action} ${toolName}?`;

    Swal.fire({
      title: `${toolName}`,
      text: confirmationMessage,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        const newState = { ...cardStates, [toolKey]: !currentState };
        setCardStates(newState);

        // Immediately save to localStorage
        try {
          localStorage.setItem("cardStates", JSON.stringify(newState));
        } catch (error) {
          console.error("Error saving card states:", error);
        }

        const status = newState[toolKey] ? "Activated" : "Deactivated";
        const currentTime = new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        const newLastUpdated = {
          ...lastUpdated,
          [toolKey]: `${toolName} was last ${status} at ${currentTime}`,
        };
        setLastUpdated(newLastUpdated);

        // Immediately save lastUpdated to localStorage
        try {
          localStorage.setItem("lastUpdated", JSON.stringify(newLastUpdated));
        } catch (error) {
          console.error("Error saving last updated:", error);
        }

        Swal.fire({
          title: "Success",
          text: `${toolName} is now ${status}.`,
          icon: "success",
          confirmButtonText: "OK",
        });
      }
    });
  };

  const handleStartJumphost = () => {
    setIsModalOpen(true);
  };

  const handleStart = async () => {
    if (!selectedAccount || !selectedRegion || !instanceId || !userIp) {
      Swal.fire({
        title: "Error",
        text: "Please select all required fields (Account ID, Region, Instance ID, and User IP)",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setIsStarting(true);
    try {
      const organizationID = localStorage.getItem("organizationID");
      // console.log("selectedAccount ----->", selectedAccount);
      // console.log("selectedRegion ----->", selectedRegion);
      // console.log("instanceId ----->", instanceId);
      // console.log("userIp ----->", userIp);
      // console.log("organizationID ----->", organizationID);
      const response = await fetch(
        "https://u3lrzohpy7.execute-api.us-west-2.amazonaws.com/prod/jump-host/start",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            organizationID,
            accountId: selectedAccount.value,
            instanceId,
            region: selectedRegion.value,
            userIP: userIp,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to start jumphost");
      }

      Swal.fire({
        title: "Success",
        text: `${data.message} Please wait for a few minutes for the instance to be started fully.`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Reset form fields
      setSelectedAccount(null);
      setSelectedRegion(null);
      setInstanceId('');
      setUserIp('');
      setIsModalOpen(false);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to start jumphost instance",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsStarting(false);
    }
  };

  const cardList = [
    { key: "tool1", name: "CloudGate", description: "Start your AWS jumphost instance on-demand." },
    { key: "tool2", name: "RDS Control", description: "Start and stop RDS instances to optimize costs." },
    { key: "tool3", name: "Security Scan", description: "Run on-demand security scans on your resources." },
    { key: "tool4", name: "EC2 Manager", description: "Manage EC2 instances across multiple regions." },
    { key: "tool5", name: "Cost Analyzer", description: "Analyze and optimize your cloud spending." },
    { key: "tool6", name: "Config Audit", description: "Audit your cloud configurations for compliance." },
  ];

  if (!connectedAccounts || connectedAccounts.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{
          backgroundImage: 'url("/images/blurred_image.png")', // Replace with your background image path
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="text-center text-dark p-4"
          style={{ backgroundColor: "rgba(170, 172, 242, 0.33)", borderRadius: "8px" }}
        >
          <h3>No Connected Accounts Found</h3>
          <p>
            To access this dashboard, please connect at least one account.
            Click the button below to get started.
          </p>
          <button className="btn text-white mt-3" style={{ backgroundColor: '#342ead' }}
            onClick={() => window.location.href = "/dashboard/connected-accounts"}
          >
            Connect Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="cloudmap-page container-fluid pb-5"
      style={{
        transition: "margin-left 0.3s ease",
        marginLeft: isSidebarOpen ? "0px" : "0",
      }}
    >
      <div className="container mb-4">
        <div className="row g-4">
          <div className="d-flex justify-content-between align-items-center mb-4 ms-2">
            <h1
              className="text-muted mb-0"
              style={{ fontWeight: "bold", fontSize: "2rem" }}
            >
              Command Center
            </h1>
            <div className="d-flex align-items-center">
              <span className="badge bg-light text-dark me-2" style={{ fontSize: "0.8rem", padding: "8px 12px", borderRadius: "20px" }}>
                <i className="fas fa-info-circle me-1"></i>
                {connectedAccounts.length} Connected Accounts
              </span>
            </div>
          </div>
          {cardList.map((card) => (
            <div className="col-md-4 col-lg-4" key={card.key}>
              <div
                className="p-4 rounded shadow"
                style={{
                  position: "relative",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                  color: "#000",
                  height: "170px",
                  borderRadius: "15px",
                  textAlign: "left",
                  transition: "all 0.3s ease",
                  fontWeight: "bold",
                  border: "1px solid #eaeaea",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  overflow: "hidden",
                }}
                onClick={() => handleCardClick(card.key, card.name)}
              >
                <span
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    padding: "5px 12px",
                    borderRadius: "20px",
                    backgroundColor: cardStates[card.key]
                      ? "#66Db3a"
                      : "#666cff",
                    color: "#fff",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {cardStates[card.key] ? "Activated" : "Deactivated"}
                </span>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                  <div 
                    style={{ 
                      width: "36px", 
                      height: "36px", 
                      borderRadius: "8px", 
                      backgroundColor: "#f0f4ff", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      marginRight: "10px"
                    }}
                  >
                    {card.key === 'tool1' && (
                      <img
                        src="/images/AWSLOGO.png"
                        alt="AWS Logo"
                        style={{
                          width: "24px",
                          height: "24px",
                          objectFit: "contain"
                        }}
                      />
                    )}
                    {card.key === 'tool2' && <i className="fas fa-database" style={{ color: "#342ead" }}></i>}
                    {card.key === 'tool3' && <i className="fas fa-shield-alt" style={{ color: "#342ead" }}></i>}
                    {card.key === 'tool4' && <i className="fas fa-server" style={{ color: "#342ead" }}></i>}
                    {card.key === 'tool5' && <i className="fas fa-chart-line" style={{ color: "#342ead" }}></i>}
                    {card.key === 'tool6' && <i className="fas fa-cogs" style={{ color: "#342ead" }}></i>}
                  </div>
                  <h3 style={{ margin: 0, color: "#342ead", fontWeight: "600" }}>
                    {card.name}
                  </h3>
                </div>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#6c757d",
                    marginBottom: "0",
                    lineHeight: "1.5",
                  }}
                >
                  {card.description}
                </p>
                <div
                  style={{
                    position: "absolute",
                    bottom: "15px",
                    left: "0",
                    right: "0",
                    padding: "0 25px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.75rem",
                      color: "#6c757d",
                      margin: 0,
                      fontStyle: "italic",
                    }}
                  >
                    {lastUpdated[card.key] || "No recent changes"}
                  </p>
                  {card.key === 'tool1' && cardStates[card.key] && (
                    <div 
                      style={{ 
                        width: "8px", 
                        height: "8px", 
                        borderRadius: "50%", 
                        backgroundColor: "#66Db3a",
                        boxShadow: "0 0 5px #66Db3a"
                      }}
                    />
                  )}
                </div>
                {card.key === 'tool1' && (
                  <>
                    {/* AWS logo is now part of the title */}
                    <button
                      className="btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartJumphost();
                      }}
                      disabled={!cardStates.tool1}
                      style={{
                        position: "absolute",
                        right: "15px",
                        bottom: "45px",
                        backgroundColor: cardStates.tool1 ? "#342ead" : "#e9ecef",
                        color: cardStates.tool1 ? "#fff" : "#6c757d",
                        border: "none",
                        borderRadius: "8px",
                        padding: "6px 12px",
                        fontSize: "0.85rem",
                        fontWeight: "500",
                        boxShadow: cardStates.tool1 ? "0 2px 8px rgba(52, 46, 173, 0.3)" : "none",
                        transition: "all 0.2s ease",
                        cursor: cardStates.tool1 ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <i className="fas fa-play-circle" style={{ marginRight: "5px" }}></i>
                      Start Jumphost
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {isModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-light">
                <h5 className="modal-title">Start Jumphost</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body py-5">
                <Select
                  className="react-select mb-3"
                  classNamePrefix="react-select"
                  options={connectedAccounts.map(account => ({
                    value: account.id,
                    label: `${account.alias || account.description} (${account.awsAccountId})`
                  }))}
                  value={selectedAccount}
                  onChange={(selected) => setSelectedAccount(selected)}
                  placeholder="Select Account"
                  isLoading={isLoadingAccounts}
                  isDisabled={isLoadingAccounts}
                />
                <Select
                  className="react-select mb-3"
                  classNamePrefix="react-select"
                  options={[
                    { value: "us-east-1", label: "US East (N. Virginia)" },
                    { value: "us-east-2", label: "US East (Ohio)" },
                    { value: "us-west-1", label: "US West (N. California)" },
                    { value: "us-west-2", label: "US West (Oregon)" },
                    { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
                    { value: "ap-northeast-3", label: "Asia Pacific (Osaka)" },
                    { value: "ap-northeast-2", label: "Asia Pacific (Seoul)" },
                    { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
                    { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
                    { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
                    { value: "ca-central-1", label: "Canada (Central)" },
                    { value: "eu-central-1", label: "Europe (Frankfurt)" },
                    { value: "eu-west-1", label: "Europe (Ireland)" },
                    { value: "eu-west-2", label: "Europe (London)" },
                    { value: "eu-west-3", label: "Europe (Paris)" },
                    { value: "eu-north-1", label: "Europe (Stockholm)" },
                    { value: "me-south-1", label: "Middle East (Bahrain)" },
                    { value: "me-central-1", label: "Middle East (UAE)" },
                    { value: "sa-east-1", label: "South America (São Paulo)" }
                  ]}
                  value={selectedRegion}
                  onChange={(selected) => setSelectedRegion(selected)}
                  placeholder="Select Region"
                />
                <input
                  className="form-control mb-3"
                  type="text"
                  placeholder="Enter Instance ID"
                  value={instanceId}
                  onChange={(e) => setInstanceId(e.target.value)}
                />
                <div className="mb-3">
                  <label className="form-label">IP Address</label>
                  <div className="input-group">
                    <span className="input-group-text">Detected: {detectedIp}</span>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Override IP (optional)"
                      value={userIp}
                      onChange={(e) => setUserIp(e.target.value)}
                    />
                  </div>
                  <small className="text-muted">Your detected IP will be used if no override is provided</small>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button
                  className="btn btn-scan"
                  onClick={handleStart}
                  disabled={isStarting}
                >
                  {isStarting ? "Starting..." : "Start"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommandCenter;
